import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import orderBy from 'lodash/orderBy';
import { combineLatest, merge, Observable, ReplaySubject, Subject as RxSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { getContainerProjectId } from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { getFileClassification } from '../../shared/file.utils';
import { Session } from '../../shared/models/session.model';
import { Subject } from '../../shared/models/subject.model';
import { replayValue } from '../../shared/observable.utils';
import { AcquisitionFilesComponent } from '../acquisition-files/acquisition-files.component';
import { SessionCheckedStatus, SessionService } from '../session.service';
import { sessionFilesQuery } from '../session.utils';
import { AcquisitionFilters, SessionDataState } from '../session-data/session-data.state';

@Component({
  selector: 'subject-sessions',
  templateUrl: './subject-sessions.component.html',
  styleUrls: ['./subject-sessions.component.scss'],
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
    AcquisitionFilesComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [SessionDataState],
})
export class SubjectSessionsComponent implements OnChanges, OnDestroy {
  private coreApi = inject(CoreApiService);
  private profileService = inject(ProfileService);
  private projectsService = inject(ProjectsService);
  private logger = inject(LoggingService);
  private sessionService = inject(SessionService);
  private state = inject(SessionDataState);

  @Input() subject: Subject;

  checkedSessions = new Map<string, SessionCheckedStatus>();
  checkedSubject = { checked: false, indeterminate: false };
  error: Error | null = null;
  fileTypes: string[] = [];
  filterFields = new FormGroup({
    description: new FormControl(''),
    classification: new FormControl(''),
    created: new FormControl(null),
    fileType: new FormControl(''),
  });
  filteredSessions$: Observable<Session[]>;
  projectId$ = new RxSubject<string>();
  subjectSessions$ = new ReplaySubject<Session[]>(1); // unfiltered sessions
  exhaustiveMode = false;

  private subscriptions = {
    changes: Subscription.EMPTY,
    checks: Subscription.EMPTY,
    permissions: Subscription.EMPTY,
    sessions: Subscription.EMPTY,
    exhaustive: Subscription.EMPTY,
  };

  constructor() {
    const profileService = this.profileService;

    this.subscriptions.permissions = this.projectId$
      .pipe(
        distinctUntilChanged(),
        switchMap((projectId) => this.projectsService.getProjectPermissions(projectId)),
        map((permissions) => new Set(permissions)),
      )
      .subscribe((permissions) => {
        this.state.projectPermissions$.next(permissions);
      });

    this.subscriptions.checks = combineLatest([
      this.sessionService.checkedSessions$.pipe(startWith(this.sessionService.checkedSessions$.value)),
      this.subjectSessions$,
    ]).subscribe(([checkedSessions, subjectSessions]) => {
      this.checkedSessions = checkedSessions;
      this.checkedSubject.checked = subjectSessions.some((session) => {
        const status = checkedSessions.get(session._id);
        return status?.checked;
      });
      this.checkedSubject.indeterminate =
        this.checkedSubject.checked &&
        subjectSessions.some((session) => {
          const status = checkedSessions.get(session._id);
          return status?.indeterminate || !status?.checked;
        });
    });
    this.subscriptions.exhaustive = profileService.exhaustiveMode$.subscribe((exhaustive) => {
      this.exhaustiveMode = exhaustive;
    });

    const filterChanges$: Observable<AcquisitionFilters> = combineLatest([
      replayValue(this.filterFields.controls.description),
      replayValue(this.filterFields.controls.classification),
      replayValue(this.filterFields.controls.created),
      replayValue(this.filterFields.controls.fileType),
    ]).pipe(
      debounceTime(200),
      map(([description, classification, created, fileType]) => ({ description, classification, created, fileType })),
    );
    filterChanges$.subscribe(this.state.acquisitionFilters$);

    this.filteredSessions$ = combineLatest([this.subjectSessions$, filterChanges$]).pipe(
      map(([sessions, filters]) => {
        if (!sessions) {
          return null;
        }
        const description = filters.description.toLowerCase();
        const classification = filters.classification.toLowerCase();
        const fileType = filters.fileType.toLowerCase();
        const filtered = sessions.map((session) => {
          const acquisitions = session.acquisitions
            .filter((acq) => {
              if (fileType && !acq.files.some((file) => (file.type || '').toLowerCase() === fileType)) {
                return false;
              }
              if (
                !acq.label.toLowerCase().includes(description) &&
                !acq.files.some((file) => file.name.toLowerCase().includes(description))
              ) {
                return false;
              }
              if (!acq.files.some((file) => getFileClassification(file).toLowerCase().includes(classification))) {
                return false;
              }
              return true;
            })
            .map((acq) => {
              if (!acq.parents) {
                return (acq = { ...acq, parents: session.parents });
              }
              return acq;
            });
          return { ...session, acquisitions };
        });
        return filtered;
      }),
    );

    this.subscriptions.changes = merge(this.sessionService.acquisitionChange$, this.state.fileChanges$).subscribe(
      () => {
        this.loadSessions();
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.subject &&
      this.subject?._id &&
      (changes.subject.firstChange || this.subject?._id !== changes.subject.previousValue?._id)
    ) {
      this.loadSessions();
      this.projectId$.next(getContainerProjectId(this.subject));
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  loadSessions(): void {
    this.error = null;
    this.subscriptions.sessions.unsubscribe();
    this.subscriptions.sessions = this.coreApi.tree
      .query<Session>(sessionFilesQuery, {
        filter: `subject=${this.subject._id}`,
        exhaustive: this.exhaustiveMode,
      })
      .subscribe({
        next: (response) => {
          const sessions = response.results;
          const fileTypes = new Set<string>();
          for (const session of sessions) {
            session.container_type = ContainerType.Session;
            for (const acquisition of session.acquisitions || []) {
              acquisition.container_type = ContainerType.Acquisition;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              acquisition.files = (acquisition.files || []).map((file: any) => ({
                ...file,
                _id: file.id,
                // tree results have a weird format, _id is an object with file_id and version, cooerce to normal File
                ...file._id,
              }));

              for (const file of acquisition.files) {
                fileTypes.add(file.type || '');
              }
            }
          }
          this.fileTypes = orderBy(Array.from(fileTypes), (str) => str.toLowerCase(), 'asc');
          this.subjectSessions$.next(sessions);
        },
        error: (error) => {
          this.logger.error(error);
          this.error = error;
        },
      });
  }

  checkSubject(checked?: boolean): void {
    this.subjectSessions$.subscribe((subjectSessions) => {
      checked = typeof checked === 'boolean' ? checked : !this.checkedSubject.checked;
      this.sessionService.checkSessions(
        subjectSessions.map((session) => session._id),
        checked,
      );
    });
  }

  isAcquisitionChecked(sessionId: string, acquisitionId: string): boolean {
    const checkedStatus = this.checkedSessions.get(sessionId);
    if (!checkedStatus) {
      return false;
    }
    if (checkedStatus.checked && !checkedStatus.indeterminate) {
      return true;
    }
    return checkedStatus.indeterminate && checkedStatus.acquisitions.has(acquisitionId);
  }

  checkAcquisition(sessionId: string, acquisitionId?: string, checked?: boolean): void {
    if (!acquisitionId) {
      this.sessionService.checkSessions([sessionId], checked);
    }
    this.subjectSessions$.subscribe((subjectSessions) => {
      const session = subjectSessions.find(({ _id }) => _id === sessionId);
      if (!session) {
        return;
      }
      const checkedStatus = this.checkedSessions.get(sessionId);
      const checkedAcqs = checkedStatus?.acquisitions || new Set();
      const sessionChecked = checkedStatus?.checked || false;
      const indeterminate = checkedStatus?.indeterminate || false;
      let acqIds = new Set(checkedAcqs);
      if (typeof checked === 'boolean') {
        acqIds[checked ? 'add' : 'delete'](acquisitionId);
      } else {
        acqIds[acqIds.has(acquisitionId) || (sessionChecked && !indeterminate) ? 'delete' : 'add'](acquisitionId);
      }
      if (acqIds.size === 0 && checkedAcqs.size === 0) {
        // we unchecked from an empty set; check all others
        acqIds = new Set(session.acquisitions.map((acq) => acq._id).filter((id) => id !== acquisitionId));
      }
      this.sessionService.checkAcquisitions(session, Array.from(acqIds));
    });
  }
}
